import {
  createContext,
  useContext,
  useId,
  useRef,
  type KeyboardEvent,
  type ReactNode,
} from 'react';
import { cn } from '../../lib/cn';

/**
 * Shared id namespace for a `Tabs` + `TabPanel` pair.
 *
 * `useId` on its own would give the tab list and the panel different prefixes,
 * which silently breaks `aria-controls` — the commonest defect in hand-written
 * tab markup. Wrapping the pair in `TabsProvider` gives them one namespace.
 */
const TabIdContext = createContext<string | null>(null);

function useTabIdBase(): string {
  const fromContext = useContext(TabIdContext);
  const fallback = useId();
  return fromContext ?? fallback;
}

export function TabsProvider({ children }: { children: ReactNode }) {
  const id = useId();
  return <TabIdContext.Provider value={id}>{children}</TabIdContext.Provider>;
}

export interface TabItem<T extends string> {
  value: T;
  label: ReactNode;
}

export interface TabsProps<T extends string> {
  label: string;
  items: readonly TabItem<T>[];
  value: T;
  onChange: (value: T) => void;
  className?: string;
}

/**
 * Tab list following the ARIA tabs pattern: one tab stop for the list, arrow
 * keys to move between tabs, and `aria-controls` pointing at the panel.
 *
 * `useTabPanelIds` below hands out the matching ids so the list and the panel
 * cannot drift apart — the usual failure mode with hand-written tab markup is an
 * `aria-controls` that references an element that no longer exists.
 */
export function Tabs<T extends string>({ label, items, value, onChange, className }: TabsProps<T>) {
  const refs = useRef<(HTMLButtonElement | null)[]>([]);
  const base = useTabIdBase();

  const move = (from: number, delta: number) => {
    const count = items.length;
    if (count === 0) return;
    const next = (from + delta + count) % count;
    const item = items[next];
    if (!item) return;
    onChange(item.value);
    refs.current[next]?.focus();
  };

  const onKeyDown = (event: KeyboardEvent<HTMLButtonElement>, index: number) => {
    if (event.key === 'ArrowRight') {
      event.preventDefault();
      move(index, 1);
    } else if (event.key === 'ArrowLeft') {
      event.preventDefault();
      move(index, -1);
    } else if (event.key === 'Home') {
      event.preventDefault();
      move(0, 0);
    } else if (event.key === 'End') {
      event.preventDefault();
      move(items.length - 1, 0);
    }
  };

  return (
    <div
      role="tablist"
      aria-label={label}
      className={cn('flex gap-1 overflow-x-auto rounded-field bg-sunken p-1', className)}
    >
      {items.map((item, index) => {
        const selected = item.value === value;
        return (
          <button
            key={item.value}
            ref={(node) => {
              refs.current[index] = node;
            }}
            type="button"
            role="tab"
            id={`${base}-tab-${item.value}`}
            aria-selected={selected}
            aria-controls={`${base}-panel-${item.value}`}
            tabIndex={selected ? 0 : -1}
            onClick={() => onChange(item.value)}
            onKeyDown={(event) => onKeyDown(event, index)}
            className={cn(
              'shrink-0 rounded-[0.5rem] px-3 py-1.5 text-sm font-medium transition-colors duration-150',
              selected ? 'bg-card text-strong shadow-card' : 'text-muted hover:text-strong'
            )}
          >
            {item.label}
          </button>
        );
      })}
    </div>
  );
}

export interface TabPanelProps {
  value: string;
  children: ReactNode;
  className?: string;
}

export function TabPanel({ value, children, className }: TabPanelProps) {
  const base = useTabIdBase();
  return (
    <div
      role="tabpanel"
      id={`${base}-panel-${value}`}
      aria-labelledby={`${base}-tab-${value}`}
      // Focusable so a keyboard user can Tab from the tab list straight into the
      // panel content, which is where the copy button lives.
      tabIndex={0}
      className={cn('focus-visible:outline-none', className)}
    >
      {children}
    </div>
  );
}
